<template>
  <div class="p-4 rounded-lg border border-gray-600 dark:border-gray-700">
    <SimpleSkeleton
      v-if="consultando"
      :options="getSkeleton(1, 1, { anchoC: 'width:100%', anchoF: 'width:100%', altoF: 'h-72' })"
    />
    <div v-else-if="editable">
      <div class="mb-3 text-sm font-semibold uppercase text-gray-800 dark:text-gray-200">
        {{ tituloVistaPrevia }}
      </div>
      <div class="flex h-72 flex-col items-center justify-center gap-3 rounded-lg border border-gray-400 text-gray-500 dark:border-gray-700 dark:text-gray-400">
        <component :is="iconoGrafica" class="size-12 text-blue-500" />
        <p class="px-6 text-center text-sm">
          {{ mensajeVistaPreviaDisenador }}
        </p>
      </div>
    </div>
    <div
      v-else-if="!puedeConsultar"
      class="py-4 text-sm text-gray-500 dark:text-gray-400"
    >
      {{ mensajeNoDisponible }}
    </div>
    <div
      v-else-if="error"
      class="p-4 text-center"
    >
      <p class="mb-3 text-sm text-red-600 dark:text-red-400">
        {{ error }}
      </p>
      <ButtonDefault color="blue" @click="cargarGrafica">
        Reintentar
      </ButtonDefault>
    </div>
    <template v-else-if="resultadoGrafica">
      <div class="mb-3 text-sm font-semibold uppercase text-gray-800 dark:text-gray-200">
        {{ resultadoGrafica.titulo }}
      </div>
      <ListaSqlGrafica
        :resultado="resultadoGrafica"
        :tipo-grafica="tipoGrafica"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { ChartBarIcon, ChartPieIcon } from '@heroicons/vue/24/outline'
import type { IFormComponent } from '@/modules/Configuracion/interfaces/IFormComponent'
import type { TipoGraficaListaSql } from '@/modules/Configuracion/interfaces'
import { useListaSql } from '@/modules/Configuracion/composables/useListaSql'
import { obtenerTituloFallbackListaSql } from '@/modules/Configuracion/utils/listaSql'
import { getSkeleton } from '@/modules/Core/usings/generales'
import ListaSqlGrafica from '@/modules/Core/components/ListaSql/ListaSqlGrafica.vue'
import SimpleSkeleton from '@/modules/Core/components/general/SimpleSkeleton.vue'

const props = defineProps<{
  comp: IFormComponent
  tipoGrafica: TipoGraficaListaSql
  cittId?: number
  editable?: boolean
}>()

const { consultando, error, resultadoGrafica, cargarResultadoGrafica } = useListaSql()

const puedeConsultar = computed(() => {
  return !props.editable && !!props.cittId && !!props.comp.properties?.coreId
})
const iconoGrafica = computed(() => props.tipoGrafica === 'bar' ? ChartBarIcon : ChartPieIcon)
const tituloVistaPrevia = computed(() => props.comp.label || 'Consulta SQL')
const mensajeVistaPreviaDisenador = computed(() => {
  if (!props.comp.properties?.coreId) {
    return 'Selecciona un reporte para visualizar la gráfica durante la atención.'
  }
  return 'Los resultados de este reporte se visualizarán aquí como gráfica durante la atención.'
})
const mensajeNoDisponible = computed(() => {
  if (!props.comp.properties?.coreId) return 'Debe configurar un reporte para cargar la gráfica.'
  return 'Disponible únicamente dentro de una atención.'
})

const cargarGrafica = async () => {
  const coreId = props.comp.properties?.coreId
  if (!puedeConsultar.value || !coreId || !props.cittId) return

  try {
    await cargarResultadoGrafica({
      coreId,
      cittId: props.cittId,
      label: obtenerTituloFallbackListaSql(props.comp)
    }, props.tipoGrafica)
  } catch (errorConsulta) {
    console.error('Error cargando la gráfica del reporte:', errorConsulta)
  }
}

watch(
  () => [props.comp.properties?.coreId, props.cittId, props.editable, props.tipoGrafica],
  () => { void cargarGrafica() },
  { immediate: true }
)
</script>
